import React, { useImperativeHandle, useState, useRef } from 'react';
import {
    View,
    ScrollView,
    Text
} from 'react-native-web';
import PropTypes from 'prop-types';
import Lottie from 'react-lottie';

// Lotties
import * as animationData from '../../lotties/lottie_stop.json'

// Custom Components
import { Button, InScreenModal, SwitchView } from '../../components';

// Utils
import { storeData } from '../../utils/util_helpers';

// Configs
import GLOBAL from '../../configs/config_global';

function CateringStatusAlert(props, ref) {
    const {
        onAcceptingSendProposalPressed,
        onHiredPressed,
        onNotHiredPressed,
        clientPhone
    } = props;

    // region Refs
    const dontShowAgainSwitch = useRef(null);
    // endregion

    // region Local State
    const [visible, setVisible] = useState(false);
    const [isAccepting, setAccepting] = useState(false);
    // endregion

    // region Function - Show/Hide Modal
    const showModal = (isAccepting = false) => {
        setVisible(true);
        setAccepting(isAccepting);
    };
    const hideModal = () => setVisible(false);
    // endregion

    // region Expose Methods
    useImperativeHandle(
        ref,
        () => ({ showModal, hideModal }),
        []
    )
    // endregion

    // region Render Accepting Buttons
    const logDoNotShowAgain = () => {
        // Mark don't show again
        if (dontShowAgainSwitch.current?.getValue()) {
            storeData('cateringAcceptAlertShown', JSON.stringify({ shown: true })).then(() => {});
        }
    };

    const renderAcceptingButtons = () => (
        <View style={{ width: '100%' }}>
            {/* Send Proposal */}
            <Button
                onPress={() => {
                    hideModal();
                    logDoNotShowAgain();

                    // Callback
                    onAcceptingSendProposalPressed();
                }}
                backgroundColor={'white'}
                style={{ width: '100%', marginTop: 8, borderRadius: 8, borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER }}
                text={'Send Proposal'}
                textColor={'black'}
            />

            {/* Call Client */}
            <Button
                onPress={() => {
                    hideModal();
                    logDoNotShowAgain();
                    setTimeout(() => window.open(`tel:${clientPhone}`, '_blank'), 500);
                }}
                backgroundColor={'white'}
                style={{ width: '100%', marginTop: 8, borderRadius: 8, borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER }}
                text={'Call Client'}
                textColor={'black'}
            />

            {/* Do Later */}
            <Button
                onPress={() => {
                    hideModal();
                    logDoNotShowAgain();
                }}
                backgroundColor={GLOBAL.COLOR.MAIN}
                style={{ width: '100%', marginTop: 8, borderRadius: 8 }}
                text={'I\'ll Do This Later'}
                textColor={'white'}
            />
        </View>
    )
    // endregion

    // region MAIN RENDER
    return(
        <InScreenModal visible={visible} style={{ marginHorizontal: '5%', marginVertical: '5%', backgroundColor: 'transparent', borderColor: 'transparent' }}>
            {/* Main Content */}
            <ScrollView
                style={{ width: '100%', flex: 1, backgroundColor: 'transparent' }}
                contentContainerStyle={{ paddingHorizontal: '15%' }}
                showsVerticalScrollIndicator={false}>
                {/* Title */}
                <View style={{ alignSelf: 'stretch', paddingHorizontal: 8, paddingVertical: 16, backgroundColor: GLOBAL.COLOR.MAIN, borderTopLeftRadius: 8, borderTopRightRadius: 8, overflow: 'hidden' }}>
                    <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 15, textAlign: 'center' }}>
                        STOP! PLEASE READ!
                    </Text>
                </View>

                {/* Main View */}
                <View style={{
                    alignSelf: 'stretch', backgroundColor: 'white',
                    borderBottomRightRadius: 8, borderBottomLeftRadius: 8, borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, overflow: 'hidden',
                    flexDirection: 'row', alignItems: 'center'
                }}>
                    <View style={{ flex: 1, height: '100%', padding: 32 }}>
                        {/* Lottie */}
                        <Lottie
                            options={{
                                loop: true,
                                autoplay: true,
                                animationData: animationData,
                                rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
                            }}
                            width={240}
                            height={240}
                            isStopped={false}
                            isPaused={false}
                        />

                        {/* Description */}
                        {
                            isAccepting ?
                                <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, textAlign: 'center', fontSize: 18, lineHeight: 28 }}>
                                    You have been introduced via email to the client but this doesn't mean you are hired.
                                    {'\n\n'}
                                    You need to contact the client to sell yourself and confirm the booking. Be sure to provide pricing, menu options, request minimums, deposits, or contracts. Whatever you need to be comfortable with this event.
                                    {'\n\n'}
                                    Once the client has made their decision, come back into this lead and mark yourself as Hired or Not Hired.
                                </Text>
                                :
                                <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, textAlign: 'center', fontSize: 18, lineHeight: 28 }}>
                                    Have you confirmed directly with the client that you are hired?
                                    {'\n\n'}
                                    Make sure to secure minimums, contracts, or deposits if you require them.
                                </Text>
                        }
                    </View>


                    {/* Right View */}
                    <View style={{ width: 300, marginRight: 32 }}>
                        {/* Don't Show This Again */}
                        <SwitchView
                            ref={dontShowAgainSwitch}
                            containerStyle={{ marginHorizontal: 32, marginTop: 24, marginBottom: 24 }}
                            text={'Don\'t show this again'}
                            textStyle={{ fontSize: 18, color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY }}
                            defaultState={false}
                        />

                        {/* Buttons */}
                        {
                            isAccepting ?
                                renderAcceptingButtons()
                                :
                                <View style={{ width: '100%' }}>
                                    {/* Confirm Hired */}
                                    <Button
                                        onPress={() => {
                                            hideModal();
                                            onHiredPressed();

                                            if (dontShowAgainSwitch.current?.getValue()) {
                                                storeData('cateringHiredAlertShown', JSON.stringify({ shown: true })).then(() => {});
                                            }
                                        }}
                                        backgroundColor={GLOBAL.COLOR.MAIN}
                                        style={{ width: '100%', marginTop: 8, borderRadius: 8 }}
                                        text={'Yes, I\'ve been Hired'}
                                        textColor={'white'}
                                    />

                                    {/* Not Hired */}
                                    <Button
                                        onPress={() => {
                                            hideModal();
                                            onNotHiredPressed();

                                            if (dontShowAgainSwitch.current?.getValue()) {
                                                storeData('cateringHiredAlertShown', JSON.stringify({ shown: true })).then(() => {});
                                            }
                                        }}
                                        backgroundColor={'white'}
                                        style={{ width: '100%', marginTop: 8, borderRadius: 8, borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER }}
                                        text={'Not yet'}
                                        textColor={'black'}
                                    />
                                </View>
                        }
                    </View>


                </View>
            </ScrollView>
        </InScreenModal>
    );
    // endregion
}

// Forward Ref
CateringStatusAlert = React.forwardRef(CateringStatusAlert);

// region Props
CateringStatusAlert.propTypes = {
    onAcceptingSendProposalPressed: PropTypes.func,
    onHiredPressed: PropTypes.func,
    onNotHiredPressed: PropTypes.func,
    onDoNotShowAgainPressed: PropTypes.func,
    onDoNotShowAgainHiredPressed: PropTypes.func
}

CateringStatusAlert.defaultProps = {
    onAcceptingSendProposalPressed: () => {},
    onHiredPressed: () => {},
    onNotHiredPressed: () => {},
    onDoNotShowAgainPressed: () => {},
    onDoNotShowAgainHiredPressed: () => {}
}
// endregion

export default CateringStatusAlert;
